import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const tiers = [
  {
    label: 'Starter — 3 000 €',
    invest: 3000,
    rate: 0.20,
    color: 'bg-gray-500',
    textColor: 'text-gray-300',
    barColor: 'from-gray-500 to-gray-400',
    barW: 'w-[40%]',
  },
  {
    label: 'Advanced — 5 000 €',
    invest: 5000,
    rate: 0.30,
    color: 'bg-amber-500',
    textColor: 'text-amber-400',
    barColor: 'from-amber-500 to-orange-400',
    barW: 'w-[60%]',
  },
  {
    label: 'Premium — 10 000 €',
    invest: 10000,
    rate: 0.50,
    color: 'bg-orange-400',
    textColor: 'text-orange-400',
    barColor: 'from-orange-400 to-amber-300',
    barW: 'w-full',
  },
];

// Bar chart: simulate 12 data points for "2 working days" scenario
const timeline = [
  { label: 'D1', s: 600, a: 1500, p: 5000 },
  { label: 'D2', s: 620, a: 1520, p: 5100 },
  { label: 'D3', s: 580, a: 1480, p: 4900 },
  { label: 'D4', s: 610, a: 1510, p: 5050 },
  { label: 'D5', s: 640, a: 1560, p: 5200 },
  { label: 'D6', s: 590, a: 1490, p: 4950 },
  { label: 'D7', s: 630, a: 1540, p: 5150 },
  { label: 'D8', s: 600, a: 1500, p: 5000 },
  { label: 'D9', s: 650, a: 1570, p: 5250 },
  { label: 'D10', s: 610, a: 1510, p: 5050 },
  { label: 'D11', s: 620, a: 1530, p: 5100 },
  { label: 'D12', s: 600, a: 1500, p: 5000 },
];

const maxVal = 5500;

export default function ProfitSection() {
  const { t } = useTranslation();
  const [activeCalc, setActiveCalc] = useState(0);
  const active = tiers[activeCalc];
  const profit = active.invest * active.rate;
  const total = active.invest + profit;

  return (
    <section id="profit" className="bg-[#050508] py-16 md:py-24 px-4 md:px-6 relative overflow-hidden">
      <div className="absolute bottom-0 right-0 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-amber-500/4 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-flex items-center gap-2 text-amber-400 text-xs font-['Rajdhani'] uppercase tracking-widest mb-3 md:mb-4">
            <div className="w-8 h-[1px] bg-amber-400" />
            {t('profit.tag')}
            <div className="w-8 h-[1px] bg-amber-400" />
          </div>
          <h2 className="font-['Orbitron'] text-white font-black text-3xl md:text-5xl mb-3 md:mb-4">
            {t('profit.title1')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">{t('profit.title2')}</span>
          </h2>
          <p className="text-gray-400 text-sm md:text-base max-w-xl mx-auto font-['Rajdhani']">{t('profit.sub')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-start">
          {/* Calculator */}
          <div className="bg-[#0e0e1a] border border-white/5 rounded-2xl p-5 md:p-8">
            <div className="font-['Orbitron'] text-white font-bold text-sm md:text-base mb-5 md:mb-6 tracking-wider">
              {t('profit.calc.title')}
            </div>

            <div className="space-y-2.5 md:space-y-3 mb-6 md:mb-8">
              {tiers.map((ti, i) => (
                <button
                  key={ti.label}
                  onClick={() => setActiveCalc(i)}
                  className={`w-full flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
                    activeCalc === i ? 'border-amber-500/50 bg-amber-500/5' : 'border-white/5 hover:border-white/10 bg-transparent'
                  }`}
                >
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${ti.color}`} />
                  <span className={`font-['Rajdhani'] font-bold text-xs md:text-sm ${activeCalc === i ? ti.textColor : 'text-gray-400'}`}>
                    {ti.label}
                  </span>
                  <span className={`ml-auto font-['Orbitron'] font-black text-xs md:text-sm ${activeCalc === i ? ti.textColor : 'text-gray-600'}`}>
                    +{Math.round(ti.rate * 100)}%
                  </span>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-2.5 md:gap-4">
              {[
                { labelKey: 'profit.calc.invested', value: `${active.invest.toLocaleString()}€`, border: 'border-white/5', textClass: 'text-white' },
                { labelKey: 'profit.calc.profit', value: `+${profit.toLocaleString()}€`, border: 'border-amber-500/20', textClass: active.textColor },
                { labelKey: 'profit.calc.total', value: `${total.toLocaleString()}€`, border: 'border-white/5', textClass: 'text-white' },
              ].map((item) => (
                <div key={item.labelKey} className={`text-center bg-black/30 rounded-xl p-2.5 md:p-4 border ${item.border}`}>
                  <div className="text-gray-500 text-[10px] md:text-xs font-['Rajdhani'] uppercase mb-1">{t(item.labelKey)}</div>
                  <div className={`font-['Orbitron'] font-bold text-base md:text-lg ${item.textClass}`}>{item.value}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 md:mt-6 space-y-2.5 md:space-y-3">
              {tiers.map((ti) => (
                <div key={ti.label} className="space-y-1">
                  <div className="flex justify-between text-xs font-['Rajdhani']">
                    <span className={ti.textColor}>{ti.label}</span>
                    <span className={ti.textColor}>+{Math.round(ti.rate * 100)}%</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full bg-gradient-to-r ${ti.barColor} ${ti.barW} rounded-full transition-all duration-500`} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chart */}
          <div className="bg-[#0e0e1a] border border-white/5 rounded-2xl p-5 md:p-8">
            <div className="font-['Orbitron'] text-white font-bold text-sm md:text-base mb-1 tracking-wider">
              {t('profit.chart.title')}
            </div>
            <div className="text-gray-500 text-xs font-['Rajdhani'] mb-4 md:mb-6">{t('profit.chart.sub')}</div>

            <div className="flex gap-3 md:gap-4 mb-4 md:mb-6 flex-wrap">
              {[
                { label: 'Starter', color: 'bg-gray-400' },
                { label: 'Advanced', color: 'bg-amber-500' },
                { label: 'Premium', color: 'bg-orange-400' },
              ].map((l) => (
                <div key={l.label} className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${l.color}`} />
                  <span className="text-gray-400 text-xs font-['Rajdhani']">{l.label}</span>
                </div>
              ))}
            </div>

            <div className="flex items-end gap-1 h-44 md:h-48">
              {timeline.map((row) => (
                <div key={row.label} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex flex-col gap-0.5 items-center justify-end" style={{ height: '152px' }}>
                    <div
                      className="w-full bg-gradient-to-t from-orange-500 to-amber-400 rounded-sm"
                      style={{ height: `${(row.p / maxVal) * 90}px` }}
                    />
                    <div
                      className="w-full bg-gradient-to-t from-amber-500 to-yellow-400 rounded-sm opacity-80"
                      style={{ height: `${(row.a / maxVal) * 55}px` }}
                    />
                    <div
                      className="w-full bg-gray-500 rounded-sm opacity-60"
                      style={{ height: `${(row.s / maxVal) * 22}px` }}
                    />
                  </div>
                  <div className="text-gray-600 text-[8px] font-['Rajdhani']">{row.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 md:mt-6 p-3 md:p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl text-center">
              <div className="font-['Orbitron'] text-amber-400 font-bold text-xs md:text-sm mb-1">{t('profit.max.title')}</div>
              <div className="font-['Orbitron'] text-white font-black text-2xl md:text-3xl">+5 000 €</div>
              <div className="text-gray-400 text-xs font-['Rajdhani'] mt-1">{t('profit.max.sub')}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}